import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAppData } from './AppDataContext';
import { secureGetItem, secureSetItem } from '@/lib/storage';

interface StoreContextType {
  selectedStoreIndex: number;
  setSelectedStoreIndex: (index: number) => void;
  availableStores: any[];
  selectedStore: any;
  setSelectedStoreById: (storeId: string) => void;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

const SELECTED_STORE_KEY = 'selected-store-id';

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  const { config, isInitialLoading } = useAppData();
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Get available stores (not virtual and not hidden)
  const availableStores = config?.stores?.filter((store: any) => 
    !store.virtual && !store.hidden
  ) || [];
  
  // Get selected store index based on ID
  const selectedStoreIndex = selectedStoreId 
    ? availableStores.findIndex((store: any) => store._id === selectedStoreId)
    : 0;
  
  // If the selected store ID is not found, default to 0
  const validSelectedStoreIndex = selectedStoreIndex >= 0 ? selectedStoreIndex : 0;
  

  // Initialize default store from localStorage or config.defaultStore
  useEffect(() => {
    const initializeStore = async () => {
      // Check if stores have valid data (not just empty placeholders)
      const hasValidStores = availableStores.length > 0 && 
        availableStores.some((store: any) => store.company && store.company.trim() !== '');
      
      // Wait until config is fully loaded and we have valid stores
      if (hasInitialized || isInitialLoading || !config?.stores || !hasValidStores) {
        return;
      }
      
      // First, try to load from localStorage
      const savedStoreId = await secureGetItem(SELECTED_STORE_KEY);
      
      if (savedStoreId && availableStores.some((store: any) => store._id === savedStoreId)) {
        setSelectedStoreId(savedStoreId);
        setHasInitialized(true);
      } else if (config.defaultStore !== undefined) {
        // config.defaultStore can be either an _id (string) or an index (number)
        let defaultStoreId: string | null = null;
        
        if (typeof config.defaultStore === 'string') {
          // It's an _id
          defaultStoreId = config.defaultStore;
        } else if (typeof config.defaultStore === 'number' && config.stores[config.defaultStore]) {
          // It's an index
          defaultStoreId = config.stores[config.defaultStore]._id;
        }
        
        if (defaultStoreId && availableStores.some((store: any) => store._id === defaultStoreId)) {
          setSelectedStoreId(defaultStoreId);
          await secureSetItem(SELECTED_STORE_KEY, defaultStoreId);
        } else {
          // If defaultStore is not available, use first available
          const firstStoreId = availableStores[0]._id;
          setSelectedStoreId(firstStoreId);
          await secureSetItem(SELECTED_STORE_KEY, firstStoreId);
        }
        setHasInitialized(true);
      } else {
        // No defaultStore configured, use first available
        const firstStoreId = availableStores[0]._id;
        setSelectedStoreId(firstStoreId);
        await secureSetItem(SELECTED_STORE_KEY, firstStoreId);
        setHasInitialized(true);
      }
    };
    
    initializeStore();
  }, [config, availableStores.length, hasInitialized, isInitialLoading]);

  // Function to set store by index (for backward compatibility with Select component)
  const setSelectedStoreIndex = async (index: number) => {
    const store = availableStores[index];
    if (store && store._id) {
      setSelectedStoreId(store._id);
      await secureSetItem(SELECTED_STORE_KEY, store._id);
    }
  };
  
  // Function to set store by ID
  const setSelectedStoreById = async (storeId: string) => {
    setSelectedStoreId(storeId);
    await secureSetItem(SELECTED_STORE_KEY, storeId);
  };

  // Get the selected store
  const selectedStore = availableStores[validSelectedStoreIndex] || config?.stores?.[0] || {};

  // Reset selection if the selected store ID is no longer available
  useEffect(() => {
    const resetStore = async () => {
      if (selectedStoreId && availableStores.length > 0) {
        const storeExists = availableStores.some((store: any) => store._id === selectedStoreId);
        if (!storeExists) {
          const firstStoreId = availableStores[0]._id;
          setSelectedStoreId(firstStoreId);
          await secureSetItem(SELECTED_STORE_KEY, firstStoreId);
        }
      }
    };
    resetStore();
  }, [availableStores, selectedStoreId]);

  return (
    <StoreContext.Provider value={{
      selectedStoreIndex: validSelectedStoreIndex,
      setSelectedStoreIndex,
      availableStores,
      selectedStore,
      setSelectedStoreById
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

